import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { Loader2, ArrowLeft, Check } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ANLAESSE = [
  "Geburtstag",
  "Runder Geburtstag",
  "Hochzeitstag / Jubiläum",
  "Weihnachten",
  "Valentinstag",
  "Muttertag / Vatertag",
  "Dankeschön",
  "Ohne besonderen Anlass",
];

const inputCls =
  "w-full bg-transparent border-0 border-b border-[#E3DECE] rounded-none focus:outline-none focus:border-[#BA6D52] focus:ring-0 px-0 py-3 text-[#2C2A26] placeholder:text-[#A8A399] text-base font-light transition-colors";

const labelCls =
  "block text-xs tracking-[0.2em] uppercase text-[#8A7A68] mb-2";

export default function GutscheinKaufen() {
  const { productType } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  const [form, setForm] = useState({
    amount_eur: "",
    buyer_first_name: "",
    buyer_last_name: "",
    buyer_email: "",
    buyer_phone: "",
    buyer_street: "",
    buyer_zip: "",
    buyer_city: "",
    recipient_name: "",
    occasion: "",
    dedication: "",
    agb_accepted: false,
  });

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    axios.get(`${API}/vouchers/products`).then((r) => {
      const p = r.data.find((x) => x.product_type === productType);
      if (!p) {
        toast.error("Bundle nicht gefunden");
        navigate("/gutscheine");
        return;
      }
      setProduct(p);
      setField("amount_eur", String(p.default_amount || ""));
      setLoading(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productType]);

  const canSubmit =
    form.amount_eur &&
    Number(form.amount_eur) >= 50 &&
    form.buyer_first_name.trim() &&
    form.buyer_last_name.trim() &&
    form.buyer_email.trim() &&
    form.buyer_street.trim() &&
    form.buyer_zip.trim() &&
    form.buyer_city.trim() &&
    form.agb_accepted;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) {
      toast.error("Bitte alle Pflichtfelder ausfüllen (Mindestbetrag 50 €).");
      return;
    }
    setSending(true);
    try {
      const payload = {
        product_type: productType,
        amount_eur: Number(form.amount_eur),
        buyer_first_name: form.buyer_first_name.trim(),
        buyer_last_name: form.buyer_last_name.trim(),
        buyer_email: form.buyer_email.trim(),
        buyer_phone: form.buyer_phone.trim(),
        buyer_street: form.buyer_street.trim(),
        buyer_zip: form.buyer_zip.trim(),
        buyer_city: form.buyer_city.trim(),
        recipient_name: form.recipient_name.trim(),
        occasion: form.occasion.trim(),
        dedication: form.dedication.trim(),
      };
      const res = await axios.post(`${API}/vouchers/order`, payload);
      navigate(`/gutscheine/danke/${res.data.order_number}`, {
        state: { order: res.data },
      });
    } catch (err) {
      toast.error(
        err?.response?.data?.detail ||
          "Bestellung konnte nicht angelegt werden. Bitte später erneut versuchen."
      );
    } finally {
      setSending(false);
    }
  };

  if (loading || !product) {
    return (
      <div className="min-h-screen bg-[#F5EFE3] flex items-center justify-center">
        <Loader2 className="animate-spin text-[#D68A64]" size={32} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5EFE3] pt-16 pb-24 px-6" data-testid="voucher-order-page">
      <div className="max-w-3xl mx-auto">
        <Link
          to="/gutscheine"
          className="inline-flex items-center gap-2 text-sm text-[#8A7A68] hover:text-[#D68A64] mb-8"
        >
          <ArrowLeft size={16} /> Zurück zur Auswahl
        </Link>

        <div className="text-center mb-10">
          <div className="text-xs tracking-[0.25em] uppercase text-[#8A7A68] mb-3">Gutschein</div>
          <h1 className="font-serif text-4xl md:text-5xl text-[#2C2A26] mb-3">{product.name}</h1>
          <p className="text-[#5C574E] font-light">{product.subtitle}</p>
        </div>

        {/* Info-Hinweis: Guthaben-Charakter */}
        <div className="bg-[#FBEEDF] border-2 border-[#D68A64] p-6 mb-10 text-sm text-[#5C574E] leading-relaxed" data-testid="voucher-guthaben-hinweis">
          <div className="flex items-start gap-3 mb-3">
            <div className="w-8 h-8 flex-shrink-0 rounded-full bg-[#D68A64] text-white flex items-center justify-center font-serif text-lg">i</div>
            <div>
              <p className="text-[#D68A64] font-serif text-lg mb-1">So funktioniert der Gutschein</p>
              <p className="text-xs uppercase tracking-[0.2em] text-[#8A7A68]">Bitte vor dem Kauf lesen</p>
            </div>
          </div>
          <p>
            Der Betrag, den Sie wählen, ist ein <b className="text-[#2C2A26]">Guthaben</b>. Bei Einlösung
            wird es von der individuell kalkulierten Endrechnung des Menü-Einsatzes
            (Personenzahl, Gang-Anzahl, Wareneinsatz) abgezogen. Reicht das Guthaben nicht
            aus, wird der Restbetrag bei der Einlösung fällig. Ist es höher, bleibt das
            Restguthaben bis zum Ablauf des Gutscheins verfügbar.
          </p>
        </div>

        <form onSubmit={submit} className="space-y-10">
          {/* Betrag */}
          <section className="bg-[#FBF7EC] border border-[#E3DECE] p-6 md:p-8">
            <label className={labelCls}>Guthaben-Betrag (in €)</label>
            <div className="flex items-end gap-3">
              <input
                type="number"
                min="50"
                step="10"
                value={form.amount_eur}
                onChange={(e) => setField("amount_eur", e.target.value)}
                data-testid="voucher-amount-input"
                className="w-40 bg-transparent border-0 border-b-2 border-[#D68A64] rounded-none px-0 py-2 text-4xl font-serif text-[#D68A64] focus:outline-none focus:ring-0"
                required
              />
              <span className="text-lg text-[#8A7A68] pb-2">€</span>
            </div>
            <p className="text-xs text-[#8A7A68] mt-3">
              Mindestbetrag 50 €. Der Richtwert von <b>rund {product.default_amount} €</b> deckt ein
              mehrgängiges Signature-Menü mit sorgfältig ausgewählten, frischen Zutaten ab.
              Ein <b>3-Gang-Menü</b> ist bereits ab ca. <b>{Math.round(product.default_amount * 0.55)} €</b> möglich —
              der Beschenkte entscheidet später gemeinsam mit Lukas, wie das Menü ausfallen soll.
              Sie können jeden beliebigen Betrag wählen; er wird bei der Einlösung angerechnet.
            </p>
          </section>

          {/* Käufer */}
          <section>
            <h3 className="font-serif text-xl text-[#2C2A26] mb-6 border-b border-[#E3DECE] pb-3">
              Ihre Daten (für die Rechnung)
            </h3>
            <div className="grid md:grid-cols-2 gap-x-6 gap-y-4">
              <div>
                <label className={labelCls}>Vorname *</label>
                <input type="text" required value={form.buyer_first_name} onChange={(e) => setField("buyer_first_name", e.target.value)} data-testid="voucher-first-name" className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Nachname *</label>
                <input type="text" required value={form.buyer_last_name} onChange={(e) => setField("buyer_last_name", e.target.value)} data-testid="voucher-last-name" className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>E-Mail *</label>
                <input type="email" required value={form.buyer_email} onChange={(e) => setField("buyer_email", e.target.value)} data-testid="voucher-email" className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Telefon (optional)</label>
                <input type="tel" value={form.buyer_phone} onChange={(e) => setField("buyer_phone", e.target.value)} data-testid="voucher-phone" className={inputCls} />
              </div>
              <div className="md:col-span-2">
                <label className={labelCls}>Straße & Hausnummer *</label>
                <input type="text" required value={form.buyer_street} onChange={(e) => setField("buyer_street", e.target.value)} data-testid="voucher-street" className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>PLZ *</label>
                <input type="text" required value={form.buyer_zip} onChange={(e) => setField("buyer_zip", e.target.value)} data-testid="voucher-zip" className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Ort *</label>
                <input type="text" required value={form.buyer_city} onChange={(e) => setField("buyer_city", e.target.value)} data-testid="voucher-city" className={inputCls} />
              </div>
            </div>
            <p className="text-xs text-[#8A7A68] italic mt-4">
              An diese Adresse wird auch der versiegelte Brief-Gutschein verschickt.
            </p>
          </section>

          {/* Empfänger & Widmung */}
          <section>
            <h3 className="font-serif text-xl text-[#2C2A26] mb-6 border-b border-[#E3DECE] pb-3">
              Für wen ist der Gutschein?
            </h3>
            <p className="text-sm text-[#5C574E] italic mb-6">
              Diese Angaben schreibe ich handschriftlich auf den Gutschein — deshalb sind sie optional.
              Wenn Sie hier nichts eintragen, bleibt die Widmungs-Zeile leer und Sie können später
              selbst darauf schreiben.
            </p>
            <div className="space-y-6">
              <div>
                <label className={labelCls}>Empfängername (optional)</label>
                <input type="text" placeholder="z. B. Familie Müller / Mama & Papa / Anna" value={form.recipient_name} onChange={(e) => setField("recipient_name", e.target.value)} data-testid="voucher-recipient-name" className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Anlass (optional)</label>
                <input list="voucher-occasions" type="text" placeholder="z. B. Zum 60. Geburtstag" value={form.occasion} onChange={(e) => setField("occasion", e.target.value)} data-testid="voucher-occasion" className={inputCls} />
                <datalist id="voucher-occasions">
                  {ANLAESSE.map((a) => <option key={a} value={a} />)}
                </datalist>
              </div>
              <div>
                <label className={labelCls}>Persönliche Widmung (optional)</label>
                <textarea rows={4} placeholder="Ein persönlicher Satz für die Beschenkten — max. 2–3 Zeilen." value={form.dedication} onChange={(e) => setField("dedication", e.target.value)} data-testid="voucher-dedication" className={`${inputCls} resize-none`} maxLength={300} />
                <div className="text-right text-xs text-[#A8A399] mt-1">{form.dedication.length}/300 Zeichen</div>
              </div>
            </div>
          </section>

          {/* AGB */}
          <section className="bg-[#FBF7EC] border border-[#E3DECE] p-6">
            <label className="flex items-start gap-3 text-sm text-[#5C574E] cursor-pointer">
              <input type="checkbox" checked={form.agb_accepted} onChange={(e) => setField("agb_accepted", e.target.checked)} data-testid="voucher-agb" className="mt-1 accent-[#D68A64]" required />
              <span>
                Ich akzeptiere die <Link to="/agb" className="text-[#D68A64] underline">AGB</Link> und
                die <Link to="/datenschutz" className="text-[#D68A64] underline">Datenschutzerklärung</Link>.
                Ich verstehe, dass der Gutscheinbetrag ein <b>Guthaben</b> ist, das bei Einlösung
                mit der Endrechnung des Menü-Einsatzes verrechnet wird. Der Gutschein ist
                3 Jahre gültig, keine Barauszahlung. Zahlung per Überweisung.
              </span>
            </label>
          </section>

          {/* Submit */}
          <div className="text-center">
            <button
              type="submit"
              disabled={!canSubmit || sending}
              data-testid="voucher-submit-btn"
              className="inline-flex items-center gap-3 bg-[#D68A64] hover:bg-[#C07B58] disabled:bg-[#D68A64]/40 text-white px-10 py-4 text-sm tracking-[0.2em] uppercase transition-colors"
            >
              {sending ? <><Loader2 className="animate-spin" size={16} /> Wird gesendet…</> :
                        <><Check size={16} /> Gutschein bestellen</>}
            </button>
            <p className="text-xs text-[#8A7A68] mt-4 italic">
              Nach der Bestellung erhalten Sie eine Rechnung mit IBAN &amp; Verwendungszweck per E-Mail.
              Der versiegelte Brief-Gutschein wird nach Zahlungseingang binnen 10–14 Werktagen versandt.
            </p>
          </div>
        </form>
      </div>
    </div>
  );
}
